import { useMemo, useState } from "react";
import {
  TextField,
  MenuItem,
  Select,
  Autocomplete,
  FormControl,
  InputLabel,
  Button,
  Box,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import { format, parse, isValid } from "date-fns";
import { getName } from "country-list";
import {
  ChampionsLeagueStages,
  LEAGUE_NAME_KEYWORDS,
  MatchType,
  MatchStatus,
} from "../../utils/constants";
import LookupAutocomplete from "../../components/LookupAutocomplete";
import { searchTeams } from "../../services/lookup.service";
import { searchCountries } from "../../utils/countrySearch";

const EditMatchForm = ({ match, onSave, onCancel, isUpdatePending }) => {
  const DATETIME_LOCAL_FORMAT = "yyyy-MM-dd'T'HH:mm";

  const leagueName = match.league?.name?.toLowerCase() || "";
  const isAfcon = leagueName.includes(LEAGUE_NAME_KEYWORDS.AFCON) || false;

  const inferredType =
    match.type ??
    match.matchType ??
    (match.homeCountry || match.awayCountry
      ? MatchType.COUNTRIES
      : MatchType.TEAMS);

  const toCountryValue = (code) => {
    if (!code) {
      return null;
    }
    const iso2 = String(code).toUpperCase();
    return { code: iso2, name: getName(iso2.toLowerCase()) || iso2 };
  };

  const [editValues, setEditValues] = useState(() => ({
    type: isAfcon ? MatchType.COUNTRIES : inferredType || MatchType.TEAMS,
    round: match.round ?? "",
    status: match.status ?? MatchStatus.UPCOMING,
    homeTeamId: match.homeTeam
      ? { id: match.homeTeam.id, name: match.homeTeam.name }
      : match.homeTeamId
        ? { id: match.homeTeamId, name: `Team ${match.homeTeamId}` }
        : null,
    awayTeamId: match.awayTeam
      ? { id: match.awayTeam.id, name: match.awayTeam.name }
      : match.awayTeamId
        ? { id: match.awayTeamId, name: `Team ${match.awayTeamId}` }
        : null,
    homeCountry: toCountryValue(match.homeCountry),
    awayCountry: toCountryValue(match.awayCountry),
    homeTeamScore: match.homeTeamScore ?? "",
    awayTeamScore: match.awayTeamScore ?? "",
    startAt: match.startAt
      ? format(new Date(match.startAt), DATETIME_LOCAL_FORMAT)
      : "",
  }));

  const [errors, setErrors] = useState({});

  const isCountryMatch = isAfcon || editValues.type === MatchType.COUNTRIES;

  const scoreLabelPrefix = useMemo(
    () => (isCountryMatch ? "Home" : "Home Team"),
    [isCountryMatch],
  );
  const awayScoreLabelPrefix = useMemo(
    () => (isCountryMatch ? "Away" : "Away Team"),
    [isCountryMatch],
  );

  const validate = () => {
    const next = {};

    if (!editValues.type) {
      next.type = "Match type is required";
    }

    if (isCountryMatch) {
      if (!editValues.homeCountry) {
        next.homeCountry = "Home country is required";
      }
      if (!editValues.awayCountry) {
        next.awayCountry = "Away country is required";
      }
      if (
        editValues.homeCountry?.code &&
        editValues.awayCountry?.code &&
        String(editValues.homeCountry.code).toUpperCase() ===
          String(editValues.awayCountry.code).toUpperCase()
      ) {
        next.awayCountry = "Home and away countries must be different";
      }
    } else {
      if (!editValues.homeTeamId) {
        next.homeTeamId = "Home team is required";
      }
      if (!editValues.awayTeamId) {
        next.awayTeamId = "Away team is required";
      }
      if (
        editValues.homeTeamId?.id &&
        editValues.awayTeamId?.id &&
        editValues.homeTeamId.id === editValues.awayTeamId.id
      ) {
        next.awayTeamId = "Home and away teams must be different";
      }
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = () => {
    if (!validate()) {
      return;
    }

    const payload = {
      status: editValues.status,
      type: isCountryMatch ? MatchType.COUNTRIES : MatchType.TEAMS,
    };

    if (editValues.round && editValues.round.trim() !== "") {
      payload.round = editValues.round;
    }
    if (isCountryMatch) {
      payload.homeCountry = String(
        editValues.homeCountry.code || "",
      ).toUpperCase();
      payload.awayCountry = String(
        editValues.awayCountry.code || "",
      ).toUpperCase();
    } else {
      payload.homeTeamId = editValues.homeTeamId.id;
      payload.awayTeamId = editValues.awayTeamId.id;
    }
    if (editValues.homeTeamScore !== "") {
      payload.homeTeamScore = parseInt(editValues.homeTeamScore);
    }
    if (editValues.awayTeamScore !== "") {
      payload.awayTeamScore = parseInt(editValues.awayTeamScore);
    }
    if (editValues.startAt && typeof editValues.startAt === "string") {
      const d = parse(
        editValues.startAt.trim(),
        DATETIME_LOCAL_FORMAT,
        new Date(),
      );
      if (isValid(d)) {
        payload.startAt = d.toISOString();
      }
    }

    onSave(payload);
  };

  const isChampionsLeague = leagueName.includes(
    LEAGUE_NAME_KEYWORDS.CHAMPIONS_LEAGUE,
  );

  return (
    <>
      <FormControl fullWidth margin="normal" error={!!errors.type}>
        <InputLabel id="type-label">Match Type</InputLabel>

        <Select
          labelId="type-label"
          value={isAfcon ? MatchType.COUNTRIES : editValues.type}
          onChange={(e) => {
            const nextType = e.target.value;
            setEditValues((prev) => ({
              ...prev,
              type: nextType,
              homeTeamId: null,
              awayTeamId: null,
              homeCountry: null,
              awayCountry: null,
            }));
            setErrors((prev) => ({
              ...prev,
              type: "",
              homeTeamId: "",
              awayTeamId: "",
              homeCountry: "",
              awayCountry: "",
            }));
          }}
          label="Match Type"
          disabled={isUpdatePending || isAfcon}
        >
          <MenuItem value={MatchType.TEAMS}>Teams</MenuItem>

          <MenuItem value={MatchType.COUNTRIES}>Countries</MenuItem>
        </Select>
      </FormControl>

      {isCountryMatch ? (
        <>
          <Box sx={{ mt: 2 }}>
            <LookupAutocomplete
              label="Home Country"
              value={editValues.homeCountry}
              onChange={(v) => {
                setEditValues((prev) => ({ ...prev, homeCountry: v }));
                setErrors((prev) => ({ ...prev, homeCountry: "" }));
              }}
              onSearch={searchCountries}
              disabled={isUpdatePending}
              required
              error={!!errors.homeCountry}
              helperText={errors.homeCountry}
              getOptionLabel={(option) => option.name || ""}
              isOptionEqualToValue={(option, value) =>
                option.code === value.code
              }
            />
          </Box>

          <Box sx={{ mt: 2 }}>
            <LookupAutocomplete
              label="Away Country"
              value={editValues.awayCountry}
              onChange={(v) => {
                setEditValues((prev) => ({ ...prev, awayCountry: v }));
                setErrors((prev) => ({ ...prev, awayCountry: "" }));
              }}
              onSearch={searchCountries}
              disabled={isUpdatePending}
              required
              error={!!errors.awayCountry}
              helperText={errors.awayCountry}
              getOptionLabel={(option) => option.name || ""}
              isOptionEqualToValue={(option, value) =>
                option.code === value.code
              }
            />
          </Box>
        </>
      ) : (
        <>
          <Box sx={{ mt: 2 }}>
            <LookupAutocomplete
              label="Home Team"
              value={editValues.homeTeamId}
              onChange={(v) => {
                setEditValues((prev) => ({ ...prev, homeTeamId: v }));
                setErrors((prev) => ({ ...prev, homeTeamId: "" }));
              }}
              onSearch={searchTeams}
              disabled={isUpdatePending}
              required
              error={!!errors.homeTeamId}
              helperText={errors.homeTeamId}
              getOptionLabel={(option) => option.name || ""}
            />
          </Box>

          <Box sx={{ mt: 2 }}>
            <LookupAutocomplete
              label="Away Team"
              value={editValues.awayTeamId}
              onChange={(v) => {
                setEditValues((prev) => ({ ...prev, awayTeamId: v }));
                setErrors((prev) => ({ ...prev, awayTeamId: "" }));
              }}
              onSearch={searchTeams}
              disabled={isUpdatePending}
              required
              error={!!errors.awayTeamId}
              helperText={errors.awayTeamId}
              getOptionLabel={(option) => option.name || ""}
            />
          </Box>
        </>
      )}

      {isChampionsLeague ? (
        <Autocomplete
          freeSolo
          options={Object.values(ChampionsLeagueStages)}
          value={
            Object.values(ChampionsLeagueStages).includes(editValues.round)
              ? editValues.round
              : null
          }
          inputValue={editValues.round}
          onChange={(_, newValue) =>
            setEditValues({ ...editValues, round: newValue ?? "" })
          }
          onInputChange={(_, newInputValue) =>
            setEditValues({ ...editValues, round: newInputValue })
          }
          disabled={isUpdatePending}
          renderInput={(params) => (
            <TextField
              {...params}
              fullWidth
              label="Round (optional)"
              margin="normal"
              disabled={isUpdatePending}
            />
          )}
        />
      ) : (
        <TextField
          fullWidth
          label="Round (optional)"
          value={editValues.round}
          onChange={(e) =>
            setEditValues({ ...editValues, round: e.target.value })
          }
          margin="normal"
          disabled={isUpdatePending}
        />
      )}
      <FormControl fullWidth margin="normal">
        <InputLabel>Status</InputLabel>

        <Select
          value={editValues.status}
          onChange={(e) =>
            setEditValues({ ...editValues, status: e.target.value })
          }
          label="Status"
          disabled={isUpdatePending}
        >
          <MenuItem value={MatchStatus.UPCOMING}>Upcoming</MenuItem>

          <MenuItem value={MatchStatus.LIVE}>Live</MenuItem>

          <MenuItem value={MatchStatus.FINISHED}>Finished</MenuItem>
        </Select>
      </FormControl>

      <TextField
        fullWidth
        label={`${scoreLabelPrefix} Score`}
        type="number"
        value={editValues.homeTeamScore}
        onChange={(e) =>
          setEditValues({
            ...editValues,
            homeTeamScore: e.target.value,
          })
        }
        margin="normal"
        disabled={isUpdatePending}
        inputProps={{ min: 0 }}
      />

      <TextField
        fullWidth
        label={`${awayScoreLabelPrefix} Score`}
        type="number"
        value={editValues.awayTeamScore}
        onChange={(e) =>
          setEditValues({
            ...editValues,
            awayTeamScore: e.target.value,
          })
        }
        margin="normal"
        disabled={isUpdatePending}
        inputProps={{ min: 0 }}
      />

      <TextField
        fullWidth
        label="Start At"
        type="datetime-local"
        value={editValues.startAt}
        onChange={(e) =>
          setEditValues({ ...editValues, startAt: e.target.value })
        }
        margin="normal"
        disabled={isUpdatePending}
        InputLabelProps={{ shrink: true }}
      />

      <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={isUpdatePending}
        >
          Save
        </Button>

        <Button
          variant="outlined"
          startIcon={<CancelIcon />}
          onClick={onCancel}
          disabled={isUpdatePending}
        >
          Cancel
        </Button>
      </Box>
    </>
  );
};

export default EditMatchForm;
